import {
  Address,
  Credit,
  CustomerDetails,
  EmployedDetails,
  EmploymentDetails,
  Expenditure,
  FinanceDetails,
  FinanceRate,
  Income,
  NotEmployedDetails,
  RetiredDetails,
  SelfEmployedDetails,
} from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'config';
import { pipe } from 'fp-ts/lib/function';
import {
  AdditionalEmploymentDetails,
  ApplyFormFieldValues,
  ExtendedEmploymentDetails,
} from 'pages/Apply/context';
import { cleanObject } from 'utils';

import {
  ALLIUM_TEST_MIDDLE_NAME,
  ETIKA_TEST_FIRST_NAME,
  HUMM_TEST_STREET,
  HUMM_TEST_SUB_500_STREET,
  OMNI_TEST_EMAIL,
} from './constants';

const getMonthlyIncomeFromEmploymentDetails = (
  details: ExtendedEmploymentDetails,
): number => {
  if (details.employment_status === 'employed') {
    return details.employed_details?.monthly_income_after_tax ?? 0;
  }
  if (details.employment_status === 'self_employed') {
    return details.self_employed_details?.monthly_income_after_tax ?? 0;
  }
  if (details.employment_status === 'not_employed') {
    return details.not_employed_details?.monthly_income_after_tax ?? 0;
  }
  return details.retired_details?.monthly_income_after_tax ?? 0;
};

const removeBadExpenditures = (
  expenditures: Array<Expenditure>,
): Array<Expenditure> =>
  expenditures.filter(
    (expenditure: Expenditure) =>
      expenditure?.expenditure_type && expenditure?.monthly_amount?.amount,
  );

const removeBadIncomes = (incomes: Array<Income>): Array<Income> =>
  incomes.filter(
    (income: Income) => income?.income_type && income?.monthly_amount?.amount,
  );

const formatMortgageOrRentExpenditure = (
  data: ApplyFormFieldValues,
): Expenditure | undefined => {
  const { credit, monthly_mortgage_amount: monthlyMortgageAmount } = data;
  const residentialStatus = credit.customer_details.residential_status;
  if (residentialStatus === 'owner_without_mortgage') return undefined;
  if (residentialStatus === 'owner')
    return {
      expenditure_type: 'mortgage',
      monthly_amount: {
        currency: 'GBP',
        amount: monthlyMortgageAmount ?? 0,
      },
    };
  return {
    expenditure_type: 'rent',
    monthly_amount: {
      currency: 'GBP',
      amount: credit.customer_details?.monthly_rent_amount?.amount ?? 0,
    },
  };
};

const formatFormFinanceDetails = (
  data: ApplyFormFieldValues,
): FinanceDetails => {
  // Get data needed
  const {
    credit: {
      employment_details: employmentDetails,
      additional_employment_details: additionalEmploymentDetails,
      finance_details: financeDetails,
    },
    credit_repayments: creditRepayments,
  } = data;
  // Calculate incomes
  const mainMontlyIncome =
    getMonthlyIncomeFromEmploymentDetails(employmentDetails);
  const otherMonthlyIncomes = additionalEmploymentDetails
    ? additionalEmploymentDetails.reduce((summed, details) => {
        const income = getMonthlyIncomeFromEmploymentDetails(details);
        return summed + income;
      }, 0)
    : 0;
  // Extract residential expenses
  const residentialExpense = formatMortgageOrRentExpenditure(data);
  // Get other incomes and expenditures
  const otherIncomes = removeBadIncomes(financeDetails?.incomes || []);
  const otherExpenditures = removeBadExpenditures(
    financeDetails?.expenditures || [],
  );
  return {
    bankrupcy_or_iva_in_last_5_years:
      financeDetails.bankrupcy_or_iva_in_last_5_years,
    incomes: [
      {
        income_type: 'earning_after_tax',
        monthly_amount: {
          currency: 'GBP',
          amount: mainMontlyIncome + otherMonthlyIncomes,
        },
      },
      ...otherIncomes,
    ],
    expenditures: [
      ...(residentialExpense ? [residentialExpense] : []),
      {
        expenditure_type: 'credit_card',
        monthly_amount: {
          currency: 'GBP',
          amount: creditRepayments ?? 0,
        },
      },
      ...otherExpenditures,
    ],
  };
};

export const formatShortFormCustomerDetails = (
  data: ApplyFormFieldValues,
): CustomerDetails => {
  const { credit } = data;
  const rent = (credit?.finance_details?.expenditures || [])?.find(
    (expense) => expense.expenditure_type === 'rent',
  );
  if (!rent) return credit.customer_details;
  const amount =
    credit.customer_details.monthly_rent_amount?.amount ||
    rent.monthly_amount?.amount;
  return {
    ...credit.customer_details,
    monthly_rent_amount: {
      currency: 'GBP',
      amount: amount ?? 0,
    },
  };
};

const parseLine1 = (
  line1: string,
  buildingName?: string,
  buildingNumber?: string,
) => {
  // Check if building name exists and isn't just whitespace
  const name =
    buildingName && /^\s*$/.test(buildingName) ? undefined : buildingName;
  // Check if building number exists and isn't just whitespace
  const number =
    buildingNumber && /^\s*$/.test(buildingNumber) ? undefined : buildingNumber;
  if (!name && !number) return line1;
  // Check if line 1 already includes either name or number
  if (name && line1.includes(name)) return line1;
  if (number && line1.includes(number)) return line1;
  // If building number is present, attach to line 1
  if (number) return `${number} ${line1}`;
  // If building name is present (in the absence of number), attach to line 1
  if (name) return `${name} ${line1}`;
  // If neither, return line 1 (fallback)
  return line1;
};

export const formatAddressData = (address: Address): Address => {
  const {
    building_name: buildingName,
    building_number: buildingNumber,
    postcode,
    line_1: line1,
    line_2: line2,
    city,
    county,
    country,
    months_at_address: monthsAtAddress,
  } = address;

  const parsedLine1 = parseLine1(line1, buildingName, buildingNumber);

  return cleanObject({
    ...(buildingName && { building_name: buildingName }),
    ...(buildingNumber && { building_number: buildingNumber }),
    line_1: parsedLine1,
    line_2: line2,
    city,
    county,
    country,
    postcode: postcode.toUpperCase(),
    months_at_address: monthsAtAddress,
  });
};

export const formatAllAddressesData = (
  currentAddress: Address,
  previousAddress1?: Address,
  previousAddress2?: Address,
  goodsDeliveryAddress?: Address,
) => {
  const includePrevious1 = !!previousAddress1;
  const includePrevious2 = !!previousAddress2;
  const hasGoodsDeliveryAddress = !!goodsDeliveryAddress;

  const formattedCurrent = formatAddressData(currentAddress);

  return {
    current_address: formattedCurrent,
    goods_delivery_address: hasGoodsDeliveryAddress
      ? formatAddressData(goodsDeliveryAddress)
      : formattedCurrent,
    ...(includePrevious1 && {
      previous_address_one: formatAddressData(previousAddress1),
    }),
    ...(includePrevious2 && {
      previous_address_two: formatAddressData(previousAddress2),
    }),
  };
};

export const formatFormCustomerDetails = (
  data: ApplyFormFieldValues,
  shortForm = false,
): CustomerDetails => {
  if (shortForm) return formatShortFormCustomerDetails(data);
  const {
    credit: { customer_details: details },
  } = data;
  const {
    current_address: currentAddress,
    previous_address_one: previousAddressOne,
    previous_address_two: previousAddressTwo,
    goods_delivery_address: goodsDeliveryAddress,
    date_of_birth: dateOfBirth,
    ...rest
  } = details;
  const addresses = formatAllAddressesData(
    currentAddress,
    previousAddressOne,
    previousAddressTwo,
    goodsDeliveryAddress,
  );
  const [day, month, year] = dateOfBirth.split('/');
  return cleanObject({
    ...rest,
    date_of_birth: new Date([year, month, day].join('-')).toISOString(),
    ...addresses,
  });
};

export type SpecificEmployedDetails = {
  employed_details: EmployedDetails;
};

export type SpecificSelfEmployedDetails = {
  self_employed_details: SelfEmployedDetails;
};

export type SpecificNotEmployedDetails = {
  not_employed_details: NotEmployedDetails;
};

export type SpecificRetiredDetails = {
  retired_details: RetiredDetails;
};

export type SpecificEmploymentDetails =
  | SpecificEmployedDetails
  | SpecificSelfEmployedDetails
  | SpecificNotEmployedDetails
  | SpecificRetiredDetails;

export const formatSpecificEmployedDetails = (
  details: ExtendedEmploymentDetails,
  shortForm = false,
): SpecificEmployedDetails => {
  const {
    employer_address: employerAddress,
    placeholder,
    monthly_income_after_tax: income,
    ...rest
  } = details.employed_details as EmployedDetails & AdditionalEmploymentDetails;
  return {
    employed_details: {
      ...rest,
      ...(!shortForm &&
        employerAddress && {
          employer_address: formatAddressData(employerAddress),
        }),
    },
  };
};

export const formatSpecificSelfEmployedDetails = (
  details: ExtendedEmploymentDetails,
  shortForm = false,
): SpecificSelfEmployedDetails => {
  const {
    business_address: businessAddress,
    placeholder,
    monthly_income_after_tax: income,
    ...rest
  } = details.self_employed_details as SelfEmployedDetails &
    AdditionalEmploymentDetails;
  return {
    self_employed_details: {
      ...rest,
      ...(!shortForm &&
        businessAddress && {
          business_address: formatAddressData(businessAddress),
        }),
    },
  };
};

export const formatSpecificNotEmployedDetails = (
  details: ExtendedEmploymentDetails,
): SpecificNotEmployedDetails => {
  const {
    placeholder,
    monthly_income_after_tax: income,
    ...rest
  } = details.not_employed_details as NotEmployedDetails &
    AdditionalEmploymentDetails;
  return {
    not_employed_details: {
      ...rest,
    },
  };
};

export const formatSpecificRetiredDetails = (
  details: ExtendedEmploymentDetails,
): SpecificRetiredDetails => {
  const {
    placeholder,
    monthly_income_after_tax: income,
    ...rest
  } = details.retired_details as RetiredDetails & AdditionalEmploymentDetails;
  return {
    retired_details: {
      ...rest,
    },
  };
};

export const formatSpecificEmploymentDetails = (
  details: ExtendedEmploymentDetails,
  shortForm = false,
): SpecificEmploymentDetails => {
  if (details.employment_status === 'employed')
    return formatSpecificEmployedDetails(details, shortForm);
  if (details.employment_status === 'self_employed')
    return formatSpecificSelfEmployedDetails(details, shortForm);
  if (details.employment_status === 'not_employed')
    return formatSpecificNotEmployedDetails(details);
  return formatSpecificRetiredDetails(details);
};

export const formatFormEmploymentDetails = (
  details: ExtendedEmploymentDetails,
  shortForm = false,
): EmploymentDetails => {
  const {
    employment_status: employmentStatus,
    gross_annual_income: grossAnnualIncome,
    household_annual_income: householdAnnualIncome,
  } = details;
  const specificEmploymentDetails = formatSpecificEmploymentDetails(
    details,
    shortForm,
  );
  return {
    employment_status: employmentStatus,
    gross_annual_income: grossAnnualIncome,
    household_annual_income: householdAnnualIncome,
    ...specificEmploymentDetails,
  };
};

export const formatFormAdditionalEmploymentDetails = (
  additionalEmploymentDetails: ExtendedEmploymentDetails[] | undefined,
  shortForm = false,
): EmploymentDetails[] | undefined => {
  if (!additionalEmploymentDetails) return undefined;
  return additionalEmploymentDetails.map((detail) =>
    formatFormEmploymentDetails(detail, shortForm),
  );
};

export const formatRawData = (
  data: ApplyFormFieldValues,
  financeRate: FinanceRate,
  shortForm = false,
): Credit | undefined => {
  const {
    credit: {
      bank_account: bankAccount,
      employment_details: formEmploymentDetails,
      additional_employment_details: formAdditionalEmploymentDetails,
    },
  } = data;
  const financeDetails = formatFormFinanceDetails(data);
  const customerDetails = formatFormCustomerDetails(data, shortForm);
  const employmentDetails = formatFormEmploymentDetails(
    formEmploymentDetails,
    shortForm,
  );
  const additionalEmploymentDetails = formatFormAdditionalEmploymentDetails(
    formAdditionalEmploymentDetails,
    shortForm,
  );
  return {
    finance_option: financeRate,
    bank_account: bankAccount,
    finance_details: financeDetails,
    customer_details: customerDetails,
    employment_details: employmentDetails,
    ...(additionalEmploymentDetails && {
      additional_employment_details: additionalEmploymentDetails,
    }),
  };
};

const addEtikaTestData = (credit: Credit, status: string) => {
  const firstName = ETIKA_TEST_FIRST_NAME?.[status];
  if (!firstName) return credit;
  const {
    maiden_name: maidenName,
    middle_name: middleNameC,
    ...restCustomer
  } = credit.customer_details;
  return {
    ...credit,
    customer_details: {
      ...restCustomer,
      first_name: firstName,
    },
  };
};

const addAlliumTestData = (credit: Credit, status: string) => {
  const middleName = ALLIUM_TEST_MIDDLE_NAME?.[status];
  if (!middleName) return credit;
  const {
    maiden_name: maidenName,
    middle_name: middleNameC,
    ...restCustomer
  } = credit.customer_details;
  return {
    ...credit,
    customer_details: {
      ...restCustomer,
      middle_name: middleName,
    },
  };
};

const addOmniTestData = (credit: Credit, status: string) => {
  const email = OMNI_TEST_EMAIL?.[status];
  if (!email) return credit;
  const {
    maiden_name: maidenName,
    middle_name: middleName,
    ...restCustomer
  } = credit.customer_details;
  return {
    ...credit,
    customer_details: {
      ...restCustomer,
      email,
    },
  };
};

const addHummTestData = (
  credit: Credit,
  status: string,
  amount = 0,
): Credit => {
  const street =
    amount < 50000
      ? HUMM_TEST_SUB_500_STREET?.[status]
      : HUMM_TEST_STREET?.[status];
  if (!street) return credit;
  const {
    maiden_name: maidenName,
    middle_name: middleName,
    ...restCustomer
  } = credit.customer_details;
  return {
    ...credit,
    customer_details: {
      ...restCustomer,
      current_address: {
        ...credit.customer_details.current_address,
        city: street,
      },
    },
  };
};

const testMiddleMaidenName = (testName: string): string | undefined => {
  if (testName.toLowerCase().includes('accept')) return 'ACCEPT';
  if (testName.toLowerCase().includes('decline')) return 'DECLINE';
  if (testName.toLowerCase().includes('refer')) return 'REFER';
  if (testName.toLowerCase().includes('mixed')) return 'MIXED';
  return undefined;
};

export const replaceTestData = (credit: Credit | undefined, amount = 0) => {
  if (!credit) return credit;
  const name =
    credit?.customer_details?.middle_name ??
    credit?.customer_details?.maiden_name ??
    undefined;
  const provider = credit?.finance_option?.provider;
  if (!provider || !name) return credit;
  if (APP_ENV !== 'qa') {
    if (!name?.toLowerCase().includes('bypass-prod')) return credit;
  }
  const status = testMiddleMaidenName(name);
  if (!status) return credit;
  if (provider === 'omni') return addOmniTestData(credit, status);
  if (provider === 'allium') return addAlliumTestData(credit, status);
  if (provider === 'etika') return addEtikaTestData(credit, status);
  if (provider === 'humm') return addHummTestData(credit, status, amount);
  return credit;
};

export type FormattedBodySuccess = {
  type: 'success';
  body: Credit;
  error?: undefined;
};

export type FormattedBodyError = {
  type: 'error';
  body?: undefined;
  error: string;
  trace: string;
};

export type FormattedBodyResult = FormattedBodySuccess | FormattedBodyError;

export const formatDataForApi = (
  data: ApplyFormFieldValues,
  financeRate: FinanceRate,
  shortForm = false,
  amount = 0,
): FormattedBodyResult => {
  try {
    const body = pipe(
      {},
      () => formatRawData(data, financeRate, shortForm),
      (credit) => replaceTestData(credit, amount),
      cleanObject,
    );
    if (!body)
      return {
        type: 'error',
        error: 'No body returned from formatter but no error hit in formatting',
        trace: 'No trace',
      };
    return {
      type: 'success',
      body,
    };
  } catch (e) {
    // eslint-disable-next-line
    console.log(`Error in formatting:\n${e}`);
    return {
      type: 'error',
      error: 'Error encountered in formatting form values:\n',
      trace: `${e}`,
    };
  }
};
